import api from './api';

export interface QuoteResponse {
  contractorId: string;
  contractorName: string;
  contractorEmail: string;
  contractorPhone?: string;
  amount: number;
  notes?: string;
  respondedAt: string;
  status: 'pending' | 'accepted' | 'declined';
  isAgencyQuote?: boolean;
}

export interface JobQuote {
  jobId: string;
  jobType: string;
  jobNotes?: string;
  agencyQuote?: {
    amount: number;
    notes?: string;
    sentAt: string;
  };
  contractorResponses: QuoteResponse[];
  acceptedQuote?: {
    contractorId: string;
    contractorName: string;
    amount: number;
    notes?: string;
    decidedAt: string;
  };
  status: 'draft' | 'sent' | 'responded' | 'accepted' | 'declined';
  createdAt: string;
}

// Accept a specific contractor's quote
export const acceptContractorQuote = async (
  agencySlug: string, 
  jobId: string, 
  contractorId: string
): Promise<any> => {
  const url = `/agencies/${agencySlug}/jobs/${jobId}/contractors/${contractorId}/quote/accept`;
  console.log('Accepting quote with URL:', url);
  try {
    const response = await api.post(url);
    return response.data;
  } catch (err: any) {
    // Fallback to generic accept endpoint if specific route not found
    if (err?.response?.status === 404) {
      const fallbackUrl = `/agencies/${agencySlug}/jobs/${jobId}/quote/accept`;
      console.warn('Specific accept endpoint not found. Falling back to:', fallbackUrl);
      const response = await api.post(fallbackUrl, { contractorId });
      return response.data;
    }
    throw err;
  }
};

// Decline a specific contractor's quote
export const declineContractorQuote = async (
  agencySlug: string, 
  jobId: string, 
  contractorId: string
): Promise<any> => {
  const response = await api.post(
    `/agencies/${agencySlug}/jobs/${jobId}/contractors/${contractorId}/quote/decline`
  );
  return response.data;
};

// Get all quotes for a property (with contractor responses)
export const getPropertyQuotes = async (
  agencySlug: string, 
  propertyId: string
): Promise<JobQuote[]> => {
  const response = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/quotes`);
  return response.data;
};

// Send quotes to multiple contractors
export const sendQuotesToContractors = async (
  agencySlug: string,
  jobId: string,
  contractorIds: string[],
  amount: number,
  notes?: string
): Promise<any> => {
  const response = await api.post(`/agencies/${agencySlug}/jobs/${jobId}/quotes/send`, {
    contractorIds,
    amount,
    notes
  });
  return response.data;
};

// Get quote comparison data
export const getQuoteComparison = (responses: QuoteResponse[]) => {
  if (responses.length <= 1) return null;
  
  const amounts = responses.map(r => r.amount);
  const min = Math.min(...amounts);
  const max = Math.max(...amounts);
  const avg = amounts.reduce((a, b) => a + b, 0) / amounts.length;
  
  return { min, max, avg, count: amounts.length };
};

// Format currency for display
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(amount);
};

// Get status color for UI
export const getQuoteStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
  switch (status) {
    case 'accepted': return 'success';
    case 'declined': return 'error';
    case 'pending': return 'warning';
    default: return 'default';
  }
};

// Get status icon for UI
export const getQuoteStatusIcon = (status: string): string => {
  switch (status) {
    case 'accepted': return 'check_circle';
    case 'declined': return 'cancel';
    case 'pending': return 'visibility';
    default: return 'help';
  }
};


// Resend a specific contractor invite for a job
export const resendContractorInvite = async (
  agencySlug: string,
  jobId: string,
  contractorId: string
): Promise<any> => {
  const response = await api.post(
    `/agencies/${agencySlug}/jobs/${jobId}/contractors/${contractorId}/invite/resend`
  );
  return response.data;
};

export const resendInviteByInviteId = async (
  agencySlug: string,
  jobId: string,
  inviteId: string
): Promise<any> => {
  const response = await api.post(
    `/agencies/${agencySlug}/jobs/${jobId}/invites/${inviteId}/resend`
  );
  return response.data;
};

export const cancelContractorInvite = async (
  agencySlug: string,
  jobId: string,
  contractorId: string
): Promise<any> => {
  const response = await api.post(
    `/agencies/${agencySlug}/jobs/${jobId}/contractors/${contractorId}/invite/cancel`
  );
  return response.data;
};

export const cancelInviteByInviteId = async (
  agencySlug: string,
  jobId: string,
  inviteId: string
): Promise<any> => {
  const response = await api.post(
    `/agencies/${agencySlug}/jobs/${jobId}/invites/${inviteId}/cancel`
  );
  return response.data;
};

